import { useEffect, useId, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { BuscaRemota, Rotulo } from '../../componentes/formulario';
import { LinkParceiro } from '../../componentes/link-parceiro';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, post } from '../../nucleo/api';
import { nomeParceiro } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CAMINHO_EMPRESA_CRM, useFormularioParceiro } from './FichaLateral';
import type { Oportunidade } from './tipos';

/**
 * Lista de 03/11, item 3: as empresas adicionais da negociação, abaixo da principal (que segue na seção Empresa). Cada uma abre a
 * página dela no CRM e sai com o X; "Adicionar outra empresa" busca no Cadastros ou cria a empresa sem sair da ficha. Mesma
 * permissão da rota (`pipeline.editar`) e, como no contato, ler o Cadastros (a rota recusa sem ele).
 */
export function EmpresasAdicionais({ o, lista, podeEditar, aoMudar }: { o: Oportunidade; lista: { parceiro_id: string; nome: string }[]; podeEditar: boolean; aoMudar: () => Promise<void> }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const id = useId();
  const Formulario = useFormularioParceiro();
  const pode = podeEditar && s.pode('pipeline.editar') && s.pode('cadastros.ver');
  const [adicionando, setAdicionando] = useState(false);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [nova, setNova] = useState(false);
  const botaoAbrir = useRef<HTMLButtonElement>(null);
  const idBusca = `${id}-empresa-adicional`;
  const acao = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: aoMudar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Abrir pelo teclado leva o foco à busca.
  useEffect(() => {
    if (adicionando) document.getElementById(idBusca)?.focus();
  }, [adicionando, idBusca]);
  const fechar = () => {
    setAdicionando(false);
    setEscolhida(null);
    // Cancelar devolve o foco ao botão que abriu.
    window.setTimeout(() => botaoAbrir.current?.focus(), 0);
  };
  const jaNaLista = new Set([o.parceiro_id, ...lista.map((x) => x.parceiro_id)]);
  if (!lista.length && !pode) return null;
  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-borda pt-2" data-empresas-adicionais="">
      <p className="px-2 text-xs font-medium text-tinta-3">{t('crm.ficha.empresasAdicionais')}</p>
      {lista.length === 0 && <p className="px-2 text-sm text-tinta-3">{t('crm.ficha.semEmpresasAdicionais')}</p>}
      {lista.map((x) => (
        <div key={x.parceiro_id} className="flex items-center justify-between gap-2 rounded-lg border border-borda p-2 text-sm" data-empresa-adicional={x.parceiro_id}>
          <span className="min-w-0 truncate">
            <LinkParceiro id={x.parceiro_id} nome={x.nome} para={CAMINHO_EMPRESA_CRM(x.parceiro_id)} title={nomeParceiro(x.nome)} className="font-semibold text-acento-forte hover:underline" />
          </span>
          {pode && (
            <Botao
              tamanho="sm"
              variante="fantasma"
              aria-label={t('crm.ficha.removerEmpresa', { nome: nomeParceiro(x.nome) })}
              title={t('crm.ficha.removerEmpresa', { nome: nomeParceiro(x.nome) })}
              icone={<X className="size-4" />}
              disabled={acao.isPending}
              onClick={() => acao.mutate(() => del(`/pipeline/oportunidades/${o.id}/empresas/${x.parceiro_id}`))}
              data-remover-empresa-adicional=""
            />
          )}
        </div>
      ))}
      {pode &&
        (adicionando ? (
          <div className="flex flex-col gap-2 rounded-lg border border-borda bg-sup-2 p-2">
            <Rotulo id={idBusca} rotulo={t('crm.ficha2.empresaExistente')}>
              <BuscaRemota id={idBusca} def={{ caminho: '/cadastros/parceiros', params: { tipo: 'empresa' }, rotulo: (i) => nomeParceiro(String(i.nome)) }} valor={escolhida} aoMudar={(v) => setEscolhida(v)} />
            </Rotulo>
            {Formulario && s.pode('cadastros.criar') && (
              <button type="button" className="flex min-h-11 w-fit items-center gap-1 text-sm font-medium text-acento-forte hover:underline md:min-h-9" onClick={() => setNova(true)} data-criar-empresa-adicional="">
                <Plus className="size-4" aria-hidden />
                {t('crm.ficha2.criarEmpresa')}
              </button>
            )}
            <div className="flex justify-end gap-2">
              <Botao tamanho="sm" variante="fantasma" onClick={fechar}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao
                tamanho="sm"
                variante="secundario"
                disabled={!escolhida || jaNaLista.has(escolhida)}
                carregando={acao.isPending}
                data-adicionar-empresa-confirmar=""
                onClick={() =>
                  acao.mutate(async () => {
                    await post(`/pipeline/oportunidades/${o.id}/empresas`, { parceiro_id: escolhida });
                    setAdicionando(false);
                    setEscolhida(null);
                  })
                }
              >
                {t('crm.ficha.adicionar')}
              </Botao>
            </div>
          </div>
        ) : (
          <button ref={botaoAbrir} type="button" className="flex min-h-11 w-fit items-center gap-1 px-2 text-sm font-medium text-acento-forte hover:underline md:min-h-9" onClick={() => setAdicionando(true)} aria-expanded={adicionando} data-adicionar-empresa-adicional="">
            <Plus className="size-4" aria-hidden />
            {t('crm.ficha.adicionarEmpresa')}
          </button>
        ))}
      {Formulario && nova && (
        <Formulario
          tipo="empresa"
          aberto
          aoFechar={() => setNova(false)}
          aoSalvar={(nid) =>
            acao.mutate(async () => {
              await post(`/pipeline/oportunidades/${o.id}/empresas`, { parceiro_id: nid });
              setNova(false);
              setAdicionando(false);
            })
          }
        />
      )}
    </div>
  );
}
